import React, { useState, useEffect } from 'react';
import { API_BASE, supabase } from '../../App';
import { toast } from 'react-toastify';

const PaymentMethodsManager = ({ session }) => {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newMethod, setNewMethod] = useState({
        type: 'upi', // upi, qr, bank
        value: '', // upi id or bank details
        qrFile: null,
        qrUrl: ''
    });

    useEffect(() => {
        fetchMethods();
    }, []);

    const fetchMethods = async () => {
        try {
            const token = session?.access_token || localStorage.getItem('admin_token');
            const response = await fetch(`${API_BASE}/payments/admin/methods`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setMethods(data);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load payment methods');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file) return null;

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64Image = reader.result;
                try {
                    const token = session?.access_token || localStorage.getItem('admin_token');
                    const response = await fetch(`${API_BASE}/payments/admin/upload`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            image: base64Image,
                            fileName: file.name
                        })
                    });

                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err.error || 'Upload failed');
                    }

                    const data = await response.json();
                    resolve(data.url);
                } catch (error) {
                    console.error('Upload failed:', error);
                    toast.error('Failed to upload QR code');
                    resolve(null);
                }
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let details = {};

            if (newMethod.type === 'qr') {
                if (!newMethod.qrFile) {
                    toast.error('Please upload a QR Code image');
                    setLoading(false);
                    return;
                }
                const qrUrl = await handleFileUpload(newMethod.qrFile);
                if (!qrUrl) {
                    setLoading(false);
                    return;
                }
                details = { qr_url: qrUrl, label: newMethod.value || 'Scan this QR' };
            } else if (newMethod.type === 'upi') {
                if (!newMethod.value) {
                    toast.error('Enter UPI ID');
                    setLoading(false);
                    return;
                }
                details = { value: newMethod.value };
            } else {
                details = { value: newMethod.value };
            }

            const token = session?.access_token || localStorage.getItem('admin_token');
            const response = await fetch(`${API_BASE}/payments/methods`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: newMethod.type,
                    details
                })
            });

            if (!response.ok) throw new Error('Failed to add method');

            toast.success('Payment method added successfully');
            setShowAddModal(false);
            setNewMethod({ type: 'upi', value: '', qrFile: null, qrUrl: '' });
            fetchMethods();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (id, currentStatus) => {
        try {
            const token = session?.access_token || localStorage.getItem('admin_token');
            await fetch(`${API_BASE}/payments/methods/${id}/toggle`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ is_active: !currentStatus })
            });
            fetchMethods();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this payment method?')) return;
        try {
            const token = session?.access_token || localStorage.getItem('admin_token');
            await fetch(`${API_BASE}/payments/methods/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            fetchMethods();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>💳 Payment Methods</h3>
                <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+ Add Method</button>
            </div>

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Details</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {methods.map(m => (
                            <tr key={m.id}>
                                <td style={{ textTransform: 'uppercase', fontWeight: 'bold' }}>{m.type}</td>
                                <td>
                                    {m.type === 'qr' ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <img src={m.details.qr_url} alt="QR" style={{ width: '50px', height: '50px', borderRadius: '4px' }} />
                                            <span>{m.details.label}</span>
                                        </div>
                                    ) : (
                                        <span>{m.details.value}</span>
                                    )}
                                </td>
                                <td>
                                    <span className={`badge badge-${m.is_active ? 'success' : 'warning'}`}>
                                        {m.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => handleToggle(m.id, m.is_active)}
                                        style={{ marginRight: '5px' }}
                                    >
                                        {m.is_active ? 'Disable' : 'Enable'}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(m.id)}
                                    >
                                        🗑️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Add Payment Method</h3>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleAddSubmit}>
                            <div className="form-group">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-input"
                                    value={newMethod.type}
                                    onChange={e => setNewMethod({ ...newMethod, type: e.target.value })}
                                >
                                    <option value="upi">UPI ID</option>
                                    <option value="qr">QR Code</option>
                                    <option value="bank">Bank Details</option>
                                </select>
                            </div>

                            {newMethod.type === 'qr' ? (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Upload QR Code Image</label>
                                        <input
                                            type="file"
                                            className="form-input"
                                            accept="image/*"
                                            onChange={e => setNewMethod({ ...newMethod, qrFile: e.target.files[0] })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Label (Optional)</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Scan to Pay"
                                            value={newMethod.value}
                                            onChange={e => setNewMethod({ ...newMethod, value: e.target.value })}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="form-group">
                                    <label className="form-label">
                                        {newMethod.type === 'upi' ? 'UPI ID' : 'Bank Details (Account No / IFSC)'}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={newMethod.type === 'upi' ? 'example@okaxis' : 'Acc: 1234..., IFSC: ABCD001'}
                                        value={newMethod.value}
                                        onChange={e => setNewMethod({ ...newMethod, value: e.target.value })}
                                    />
                                </div>
                            )}

                            <button className="btn btn-success" type="submit" disabled={loading} style={{ width: '100%' }}>
                                {loading ? 'Saving...' : 'Add Method'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentMethodsManager;
